import axios from 'axios';

import { useAuthStore } from '@/stores/authStore';
import type { ApiResponse } from '@/types/api';
import type { BrowserSessionResponse } from '@/types/auth';
import { browserSessionClient, unwrapBrowserSession } from './browserSessionClient';
import {
  publishBrowserSessionMutation,
  type BrowserSessionMutation,
} from './browserSessionEvents';
import { withBrowserSessionLock } from './browserSessionLock';

let activeRefresh: Promise<string> | null = null;
let sessionEpoch = 0;

const SESSION_RESET_EVENT = 'soundops:auth-session-reset';
export const SESSION_UNAVAILABLE_MESSAGE =
  'The session service is temporarily unavailable.';

const noteSessionMutation = (): void => {
  sessionEpoch += 1;
};

export const announceBrowserSessionMutation = (mutation: BrowserSessionMutation): void => {
  noteSessionMutation();
  publishBrowserSessionMutation(mutation);
};

export const noteExternalBrowserSessionMutation = noteSessionMutation;

export const clearCachedSessionData = (): void => {
  window.dispatchEvent(new Event(SESSION_RESET_EVENT));
};

export const browserSessionResetEvent = SESSION_RESET_EVENT;

const requestBrowserSession = async (): Promise<BrowserSessionResponse> => {
  const response = await withBrowserSessionLock(() =>
    browserSessionClient.post<BrowserSessionResponse | ApiResponse<BrowserSessionResponse>>(
      '/api/v1/auth/browser/refresh',
    ),
  );
  const session = unwrapBrowserSession(response.data);
  if (!session.access_token || !session.user) {
    throw new Error('auth_refresh_response_invalid');
  }

  return session;
};

const rotateSession = async (): Promise<string> => {
  for (;;) {
    const requestEpoch = sessionEpoch;
    let session: BrowserSessionResponse;
    try {
      session = await requestBrowserSession();
    } catch (error) {
      if (requestEpoch === sessionEpoch) throw error;
      continue;
    }
    if (requestEpoch !== sessionEpoch) {
      continue;
    }

    const authState = useAuthStore.getState();
    const nextActualOrgId =
      session.user.actual_organization_id ?? session.user.organization_id ?? null;
    const accountChanged =
      authState.user !== null &&
      (authState.user.id !== session.user.id ||
        authState.actualOrgId !== nextActualOrgId);
    if (accountChanged) clearCachedSessionData();
    if (accountChanged || authState.sessionStatus === 'checking' || !authState.isAuthenticated) {
      authState.setAuth(session.access_token, session.user);
    } else {
      authState.rotateSession(session.access_token, session.user);
    }
    return session.access_token;
  }
};

export const refreshAuthSession = (): Promise<string> => {
  if (!activeRefresh) {
    activeRefresh = rotateSession().finally(() => {
      activeRefresh = null;
    });
  }
  return activeRefresh;
};

export const restoreBrowserSession = (): Promise<string> => refreshAuthSession();

export const isRefreshSessionRejected = (error: unknown): boolean =>
  axios.isAxiosError(error) &&
  (error.response?.status === 401 || error.response?.status === 403);

export const endRejectedBrowserSession = () => {
  useAuthStore.getState().clearAuth();
  clearCachedSessionData();
};

export const markBrowserSessionUnavailable = (message = SESSION_UNAVAILABLE_MESSAGE): void => {
  const authState = useAuthStore.getState();
  if (authState.sessionStatus === 'signed-in') {
    authState.setSessionCheckError(message);
  }
};
